/**
 * Asset data transformation and manipulation utility functions
 * Used by fleet/dashboard pages
 */

import { arrayUtils } from "./utils.js";

export const TelemetrySources = {
  0: "Unknown",
  1: "Enbase",
  2: "Kodiak Connect",
  3: "Ignition",
  4: "M-LINK",
  5: "Flowco",
  6: "EMIT",
};

/** Default zoom for single-asset location context maps. */
export const ASSET_LOCATION_MAP_ZOOM = 10;

/**
 * @param {number|null|undefined} latitude
 * @param {number|null|undefined} longitude
 * @returns {boolean}
 */
export function hasAssetCoordinates(latitude, longitude) {
  return latitude != null && longitude != null;
}

/**
 * Transform API response to assets format
 * @param {Object} apiResponse - API response object
 * @returns {Array} Transformed assets array
 */
export const transformApiResponseToAssets = (apiResponse) => {
  if (
    !apiResponse
    || !apiResponse.Assets
    || !Array.isArray(apiResponse.Assets)
  ) {
    return [];
  }

  return apiResponse.Assets.map((asset) => ({
    id: asset.AssetId.toString(),
    assetNumber: asset.AssetNumber,
    name: asset.AssetNumber, // Use AssetNumber as the display name
    status: mapAlarmLevelToStatus(asset.Status),
    location: asset.Location,
    lease: asset.Lease,
    vendor: asset.Vendor,
    telemetrySource: TelemetrySources[asset.Telemetry] || "Undetermined",
    foreman: asset.ForemanName || null,
    customer: asset.CustomerName || null,
    horsepower: (() => {
      const n = Number(asset.Horsepower);
      return Number.isFinite(n) ? n : null;
    })(),
    compressorModel: asset.CompressorModel || null,
    engineModel: asset.EngineModel || null,
    lastUpdated: asset.LastUpdated
      ? new Date(asset.LastUpdated).toISOString()
      : new Date().toISOString(),
    lastFault: asset.LastFault,
    lastFaultTime: asset.LastFaultDateTime
      ? new Date(asset.LastFaultDateTime).toISOString()
      : null,
    latitude: asset.Latitude,
    longitude: asset.Longitude,
    enalysisCompanyId: asset.EnalysisCompanyId || null,
    enalysisCompressorNum: asset.EnalysisCompressorNum || null,
    optimizationEnabled: !!(
      asset.EnalysisCompanyId && asset.EnalysisCompressorNum
    ),
  }));
};

/**
 * Map alarm level to status string
 * @param {number} alarmLevel - Alarm level from API
 * @returns {string} Status string
 */
export const mapAlarmLevelToStatus = (alarmLevel) => {
  // Convert to number if it's a string
  const numericLevel =
    typeof alarmLevel === "string" ? parseInt(alarmLevel, 10) : alarmLevel;

  switch (numericLevel) {
    case -1:
      return "unknown";
    case 0:
      return "standby";
    case 1:
      return "normal";
    case 2:
      return "nodata";
    case 3:
      return "alerted";
    case 4:
      return "danger";
    case 5:
      return "offline";
    case 6:
      return "available";
    default:
      // If we don't know the status, return unknown
      return "unknown";
  }
};

/**
 * Calculate fleet statistics from assets
 * @param {Array} assetList - List of assets to calculate stats from
 * @returns {Object} Fleet statistics
 */
export const calculateFleetStats = (assetList) => {
  // Ensure assetList is always an array
  if (
    !assetList
    || !Array.isArray(assetList)
    || arrayUtils.isEmpty(assetList)
  ) {
    return {
      normal: 0,
      standby: 0,
      nodata: 0,
      danger: 0,
      alerted: 0,
      offline: 0,
      available: 0,
      unknown: 0,
      total: 0,
    };
  }

  const stats = assetList.reduce(
    (acc, asset) => {
      acc.total++;
      switch (asset.status) {
        case "normal":
          acc.normal++;
          break;
        case "standby":
          acc.standby++;
          break;
        case "danger":
          acc.danger++;
          break;
        case "alerted":
          acc.alerted++;
          break;
        case "nodata":
          acc.nodata++;
          break;
        case "offline":
          acc.offline++;
          break;
        case "available":
          acc.available++;
          break;
        case "unknown":
          acc.unknown++;
          break;
        default:
          // Handle any unknown statuses
          break;
      }
      return acc;
    },
    {
      normal: 0,
      standby: 0,
      danger: 0,
      alerted: 0,
      nodata: 0,
      offline: 0,
      available: 0,
      unknown: 0,
      total: 0,
    }
  );

  return stats;
};

/**
 * Filter assets based on search criteria
 * @param {Array} assets - Array of assets to filter
 * @param {string} searchTerm - Search term
 * @param {Function} formatTimestampForSearch - Function to format timestamp for search
 * @returns {Array} Filtered assets array
 */
export const filterAssetsBySearch = (
  assets,
  searchTerm,
  formatTimestampForSearch
) => {
  if (!searchTerm) return assets;

  const search = searchTerm.toLowerCase();

  return assets.filter((asset) => {
    return (
      asset.assetNumber?.toLowerCase().includes(search)
      || asset.lease?.toLowerCase().includes(search)
      || asset.location?.toLowerCase().includes(search)
      || asset.vendor?.toLowerCase().includes(search)
      || asset.telemetrySource?.toLowerCase().includes(search)
      || asset.status?.toLowerCase().includes(search)
      || formatTimestampForSearch(asset.lastUpdated).includes(search)
      || asset.lastFault?.toLowerCase().includes(search)
    );
  });
};

/**
 * Sort assets by column
 * @param {Array} assets - Array of assets to sort
 * @param {string} sortColumn - Column to sort by
 * @param {string} sortDirection - Sort direction ('asc' or 'desc')
 * @returns {Array} Sorted assets array
 */
export const sortAssetsByColumn = (assets, sortColumn, sortDirection) => {
  if (!Array.isArray(assets)) return [];

  return [...assets].sort((a, b) => {
    let aValue = a[sortColumn];
    let bValue = b[sortColumn];

    // Handle special cases for date/timestamp columns
    if (sortColumn === "lastUpdated" || sortColumn === "lastFaultTime") {
      aValue = new Date(aValue || 0);
      bValue = new Date(bValue || 0);
      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    }
    if (sortColumn === "optimizationEnabled") {
      const aVal = a.optimizationEnabled === true ? 1 : 0;
      const bVal = b.optimizationEnabled === true ? 1 : 0;
      const comparison = aVal - bVal;
      return sortDirection === "asc" ? comparison : -comparison;
    }
    // Use localeCompare for string sorting
    const comparison = String(aValue || "").localeCompare(String(bValue || ""));
    return sortDirection === "asc" ? comparison : -comparison;
  });
};

/**
 * Updates lastFault and lastFaultTime with the most recent downtime event for each asset.
 * This process is called enrichment elsewhere in the code.
 * @param {Array} assets - Array of assets from AssetContext
 * @param {Array} downtimeEvents - Array of downtime events grouped by asset from DowntimeContext
 * @returns {Array} Enriched assets array
 */
export const enrichAssetsWithDowntimeData = (assets, downtimeEvents) => {
  if (!Array.isArray(assets) || !Array.isArray(downtimeEvents)) {
    return assets;
  }
  const downtimeMap = downtimeEvents.reduce((acc, downtimeAsset) => {
    if (!downtimeAsset) {
      return acc;
    }
    acc[String(downtimeAsset.assetId)] = downtimeAsset;
    return acc;
  }, {});

  return assets.map((asset) => {
    const downtimeAsset = downtimeMap[String(asset.id)];

    if (
      downtimeAsset
      && Array.isArray(downtimeAsset.events)
      && downtimeAsset.events.length > 0
    ) {
      const sortedEvents = [...downtimeAsset.events].sort((a, b) => {
        const dateA = new Date(a.dateTime || 0);
        const dateB = new Date(b.dateTime || 0);
        return dateB.getTime() - dateA.getTime();
      });

      const mostRecentEvent = sortedEvents[0];

      if (mostRecentEvent) {
        return {
          ...asset,
          lastFault: mostRecentEvent.relatedAlarm || asset.lastFault,
          lastFaultTime: mostRecentEvent.dateTime || asset.lastFaultTime,
        };
      }
    }

    return asset;
  });
};

/**
 * @param {Object[]|undefined} assets
 * @param {string|undefined} id - Route param (may be encoded)
 * @returns {Object|undefined}
 */
export function findAssetByRouteId(assets, id) {
  if (!Array.isArray(assets) || id == null || id === "") return undefined;

  const routeId = decodeURIComponent(String(id));
  return assets.find(
    (asset) =>
      asset.assetNumber === routeId
      || asset.assetNumber === id
      || String(asset.id) === routeId
      || String(asset.id) === String(id)
  );
}

/**
 * Build href for an asset subpage, preserving current query params (e.g. from=overview).
 * @param {string|number} assetId
 * @param {string} segment - Route segment after /asset/:id/ (e.g. "downtime-analysis")
 * @returns {string}
 */
export function buildAssetSubpageHref(assetId, segment) {
  const encodedId = encodeURIComponent(String(assetId));
  const queryString =
    typeof window !== "undefined" ? window.location.search : "";
  return `/asset/${encodedId}/${segment}${queryString}`;
}
